// src/components/DiscountOfferModal.jsx
import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { useAppConfig } from "../ConfigContext";

function renderRichText(input) {
  const text = String(input ?? "");

  const STYLES = {
    gold:   { color: "#ffd700", fontWeight: 800 },
    red:    { color: "#ff6b6b", fontWeight: 800 },
    green:  { color: "#7CFC00", fontWeight: 800 },
    orange: { color: "#FFAF56", fontWeight: 800 },
    dim:    { opacity: 0.85 },
    center: { display: "block", textAlign: "center" },
  };

  const ALLOWED = new Set(Object.keys(STYLES));

  // Tokenize: [tag], [/tag], or plain text
  const tokens = [];
  const re = /\[\/?[a-zA-Z]+\]/g;
  let last = 0;
  let m;

  while ((m = re.exec(text)) !== null) {
    if (m.index > last) tokens.push({ type: "text", value: text.slice(last, m.index) });
    tokens.push({ type: "tag", value: m[0] });
    last = m.index + m[0].length;
  }
  if (last < text.length) tokens.push({ type: "text", value: text.slice(last) });

  const stack = [{ tag: null, children: [] }];
  let key = 0;

  for (const t of tokens) {
    if (t.type === "text") {
      stack[stack.length - 1].children.push(t.value);
      continue;
    }

    const raw = t.value.slice(1, -1).trim(); // remove []
    const isClose = raw.startsWith("/");
    const name = (isClose ? raw.slice(1) : raw).toLowerCase();

    // not allowed => keep as text
    if (!ALLOWED.has(name)) {
      stack[stack.length - 1].children.push(t.value);
      continue;
    }

    if (!isClose) {
      // open tag
      stack.push({ tag: name, children: [], key: key++ });
      continue;
    }

    // close tag: pop until matching
    let node = stack.pop();
    while (node && node.tag !== name && stack.length) {
      // unmatched: convert back to text
      stack[stack.length - 1].children.push(`[${node.tag}]`, ...node.children);
      node = stack.pop();
    }

    if (!node || node.tag !== name) {
      // no matching open: treat as text
      stack[stack.length - 1].children.push(t.value);
      continue;
    }

    stack[stack.length - 1].children.push(
      <span key={`rt-${node.key}`} style={STYLES[name]}>
        {node.children}
      </span>
    );
  }

  // unwind any unclosed tags
  while (stack.length > 1) {
    const node = stack.pop();
    stack[stack.length - 1].children.push(`[${node.tag}]`, ...node.children);
  }

  return <>{stack[0].children}</>;
}

/**
 * DiscountOfferModal
 * - Pulls `discounts` config from app config
 * - Chooses the first eligible offer
 * - Respects triggers:
 *   - RUNES_LT / SPARKS_LT
 *   - ONLY_REFERRED
 *   - COOLDOWN_HOURS
 *   - MAX_SHOWS_PER_DAY
 *   - VALID_FOR_SECONDS (soft window; we treat as: once shown, allow showing again only after it expires)
 *
 * Props:
 * - user
 * - guardedNavigate(to, source)  // same helper you already use in App.js CMS modal
 * - blocked (boolean)           // if other modals are open, don’t show this one
 */
export default function DiscountOfferModal({ user, guardedNavigate, blocked, onPurchased }) {
  const { discounts } = useAppConfig?.() || {};
  const cfgEnabled = !!(discounts?.ENABLED ?? discounts?.enabled);
  const offers = Array.isArray(discounts?.OFFERS ?? discounts?.offers)
    ? (discounts?.OFFERS ?? discounts?.offers)
    : [];

  const [open, setOpen] = useState(false);
  const [chosen, setChosen] = useState(null);
  const [expiresAtMs, setExpiresAtMs] = useState(null);
  const [tick, setTick] = useState(0);

  const [buying, setBuying] = useState(false);
  const [buyError, setBuyError] = useState("");
  const [successOpen, setSuccessOpen] = useState(false);
  const [successText, setSuccessText] = useState("");

  // Don’t spam if user closes it once during this session
  const dismissedThisBootRef = useRef(new Set());

  const token = localStorage.getItem("token");
  const userId = user?.id ? String(user.id) : null;

  const tz = "America/Sao_Paulo";

  const getServerNowMs = (res, data) => {
    // Try response header first
    const h =
      res?.headers?.get?.("x-server-time") ||
      res?.headers?.get?.("x-server-now") ||
      res?.headers?.get?.("date");
    const fromHeader = h ? new Date(h).getTime() : NaN;
    if (Number.isFinite(fromHeader)) return fromHeader;

    // Fallback to body if it contains a time-ish field
    const bodyCandidates = [
      data?.serverNowMs,
      data?.nowMs,
      data?.now,
      data?.timestamp,
      data?.time,
    ];
    for (const v of bodyCandidates) {
      const ms = typeof v === "number" ? v : new Date(v).getTime();
      if (Number.isFinite(ms)) return ms;
    }

    return null;
  };

  const userIsReferred = (u) => {
    if (!u || typeof u !== "object") return false;

    // Common keys we might see
    const direct = [
      u.referred,
      u.isReferred,
      u.onlyReferred,
      u.hasReferrer,
      u.referrerId,
      u.referrer_id,
      u.referredBy,
      u.referred_by,
      u.referrer,
      u.referrer_id,
    ];

    for (const v of direct) {
      if (v === true) return true;
      if (typeof v === "number" && v > 0) return true;
      if (typeof v === "string" && v.trim() && v.trim() !== "0") return true;
    }

    // Heuristic scan
    for (const [k, v] of Object.entries(u)) {
      const key = String(k).toLowerCase();
      if (key.includes("referr") || key.includes("referred")) {
        if (v === true) return true;
        if (typeof v === "number" && v > 0) return true;
        if (typeof v === "string" && v.trim() && v.trim() !== "0") return true;
      }
    }
    return false;
  };

  const pickEligibleOffer = (serverNowMs) => {
    if (!cfgEnabled) return null;
    if (!userId) return null;
    if (!Array.isArray(offers) || offers.length === 0) return null;

    const serverDay = new Date(serverNowMs).toLocaleDateString("en-CA", {
      timeZone: tz,
    });

    const uRunes = Number(user?.runes ?? 0);
    const uSparks = Number(user?.sparks ?? 0);
    const referred = userIsReferred(user);

  for (const o of offers) {
  if (!o || (o.ENABLED ?? o.enabled) === false) {
    console.log("[DiscountOfferModal] skip: disabled offer", o);
    continue;
  }

  const offerId = String(o.ID ?? o.id ?? "").trim();
  if (!offerId) {
    console.log("[DiscountOfferModal] skip: missing offerId", o);
    continue;
  }

  if (dismissedThisBootRef.current.has(offerId)) {
    console.log("[DiscountOfferModal] skip: dismissed this boot", offerId);
    continue;
  }

  const trig = o.TRIGGERS ?? o.triggers ?? {};
  const runesLt = Number(trig.RUNES_LT ?? trig.runesLt ?? 0);
  const sparksLt = Number(trig.SPARKS_LT ?? trig.sparksLt ?? 0);
  const onlyReferred = !!(trig.ONLY_REFERRED ?? trig.onlyReferred ?? false);
  const cooldownHours = Number(trig.COOLDOWN_HOURS ?? trig.cooldownHours ?? 0);
  const maxShowsPerDay = Number(trig.MAX_SHOWS_PER_DAY ?? trig.maxShowsPerDay ?? 0);


  // thresholds
  if (Number.isFinite(runesLt) && runesLt > 0 && !(uRunes < runesLt)) {
    console.log("[DiscountOfferModal] reject:", offerId, "runes threshold", { uRunes, runesLt });
    continue;
  }
  if (Number.isFinite(sparksLt) && sparksLt > 0 && !(uSparks < sparksLt)) {
    console.log("[DiscountOfferModal] reject:", offerId, "sparks threshold", { uSparks, sparksLt });
    continue;
  }

  // only referred
  if (onlyReferred && !referred) {
    console.log("[DiscountOfferModal] reject:", offerId, "onlyReferred=true but user not referred");
    continue;
  }

  // cooldown
  if (Number.isFinite(cooldownHours) && cooldownHours > 0) {
    const lastKey = `discount:last:${userId}:${offerId}`;
    const last = Number(localStorage.getItem(lastKey) || "0");
    if (Number.isFinite(last) && last > 0) {
      const diff = serverNowMs - last;
      if (diff < cooldownHours * 3600 * 1000) {
        console.log("[DiscountOfferModal] reject:", offerId, "cooldown active", {
          cooldownHours,
          last,
          serverNowMs,
          remainingMs: cooldownHours * 3600 * 1000 - diff,
        });
        continue;
      }
    }
  }

  // max/day
  if (Number.isFinite(maxShowsPerDay) && maxShowsPerDay > 0) {
    const dayKey = `discount:daycount:${userId}:${offerId}:${serverDay}`;
    const count = Number(localStorage.getItem(dayKey) || "0");
    if (Number.isFinite(count) && count >= maxShowsPerDay) {
      console.log("[DiscountOfferModal] reject:", offerId, "maxShowsPerDay hit", { count, maxShowsPerDay, serverDay });
      continue;
    }
  }

  // valid window
  const validForSeconds = Number(o.VALID_FOR_SECONDS ?? o.validForSeconds ?? 0);
  if (Number.isFinite(validForSeconds) && validForSeconds > 0) {
    const expKey = `discount:expire:${userId}:${offerId}`;
    const exp = Number(localStorage.getItem(expKey) || "0");
    if (Number.isFinite(exp) && exp > 0 && serverNowMs < exp) {
      console.log("[DiscountOfferModal] reject:", offerId, "still inside valid window", {
        exp,
        serverNowMs,
        remainingMs: exp - serverNowMs,
      });
      continue;
    }
  }

  console.log("[DiscountOfferModal] ✅ eligible:", offerId);
  return o;
}


    return null;
  };

  const markShown = (offer, serverNowMs) => {
    const offerId = String(offer?.ID ?? offer?.id ?? "").trim();
    if (!offerId || !userId) return;

    const serverDay = new Date(serverNowMs).toLocaleDateString("en-CA", {
      timeZone: tz,
    });

    localStorage.setItem(`discount:last:${userId}:${offerId}`, String(serverNowMs));

    const trig = offer?.TRIGGERS ?? offer?.triggers ?? {};
    const maxShowsPerDay = Number(trig.MAX_SHOWS_PER_DAY ?? trig.maxShowsPerDay ?? 0);
    if (Number.isFinite(maxShowsPerDay) && maxShowsPerDay > 0) {
      const dayKey = `discount:daycount:${userId}:${offerId}:${serverDay}`;
      const count = Number(localStorage.getItem(dayKey) || "0");
      localStorage.setItem(dayKey, String((Number.isFinite(count) ? count : 0) + 1));
    }

    const validForSeconds = Number(offer?.VALID_FOR_SECONDS ?? offer?.validForSeconds ?? 0);
    if (Number.isFinite(validForSeconds) && validForSeconds > 0) {
      const expKey = `discount:expire:${userId}:${offerId}`;
      localStorage.setItem(expKey, String(serverNowMs + validForSeconds * 1000));
    }
  };

  useEffect(() => {
  if (!open || !expiresAtMs) return;

  const id = setInterval(() => setTick((t) => t + 1), 1000);
  return () => clearInterval(id);
}, [open, expiresAtMs]);

 useEffect(() => {
  if (!open || !expiresAtMs) return;
  if (Date.now() < expiresAtMs) return;

  const offerId = String(chosen?.ID ?? chosen?.id ?? "").trim();
  if (offerId) dismissedThisBootRef.current.add(offerId);
  setOpen(false);
  setChosen(null);
  setExpiresAtMs(null);
  setTick(0);
}, [tick, open, expiresAtMs, chosen, userId]);



  const close = () => {
    const offerId = String(chosen?.ID ?? chosen?.id ?? "").trim();
    if (offerId) dismissedThisBootRef.current.add(offerId);
    setOpen(false);
    setChosen(null);
    setExpiresAtMs(null);
    setTick(0);
  };

  const hideOffer = () => {
  // hide the offer modal, but DO NOT clear chosen (success modal still needs it)
  setOpen(false);
  setExpiresAtMs(null);
  setTick(0);
};


  const pollDiscountStarsOrderPaid = async (orderId) => {
  // poll up to ~20s
  for (let i = 0; i < 20; i++) {
    try {
      const r = await fetch(
        `${process.env.REACT_APP_API_URL}/api/shop/discounts/stars/order/${orderId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const j = await r.json().catch(() => ({}));
      if (r.ok && j?.status === "paid") return true;
    } catch {}
    await new Promise((res) => setTimeout(res, 1000));
  }
  return false;
};

   const purchaseOffer = async () => {
  if (!token) return;
  if (!chosen) return;

  const offerId = String(chosen?.ID ?? chosen?.id ?? "").trim();
  if (!offerId) return;

  const priceType = String(chosen?.PRICE_TYPE ?? chosen?.priceType ?? "SPARKS").trim().toUpperCase();

  setBuyError("");
  setBuying(true);

  try {
    // ---- STARS flow ----
    if (priceType === "STARS") {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/shop/discounts/stars/create-order`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ offerId }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.ok || !data?.invoiceLink || !data?.orderId) {
        const msg = data?.error || "Failed to create Stars invoice.";
        setBuyError(msg);
        return;
      }

      const invoiceLink = data.invoiceLink;
      const orderId = data.orderId;

      // Prefer Telegram WebApp openInvoice if available
      const tg = window?.Telegram?.WebApp;
      if (tg?.openInvoice) {
       tg.openInvoice(invoiceLink, async (status) => {
  if (status === "paid") {
    // 1) confirm backend marked order as paid
    const ok = await pollDiscountStarsOrderPaid(orderId);

    if (!ok) {
      setBuyError("Payment received, but confirmation is still pending. Please reopen the offer in a few seconds.");
      return;
    }

    // 2) show success modal
    const grantType = String(chosen?.TYPE ?? chosen?.type ?? "RUNES").toUpperCase();
    const grantAmount = Number(chosen?.GRANT_AMOUNT ?? chosen?.grantAmount ?? 0);

    const prettyGrantType =
  String(grantType || "")
    .toLowerCase()
    .replace(/^./, (c) => c.toUpperCase());

setSuccessText(
  `[gold]${grantAmount} ${prettyGrantType}[/gold] Successfully Added To Your Account!`
);

    setSuccessOpen(true);

    // 3) update app balances immediately (optimistic but safe because backend confirmed paid)
    if (typeof onPurchased === "function") {
      if (grantType === "RUNES") {
        onPurchased({ runes: Number(user?.runes ?? 0) + grantAmount, sparks: Number(user?.sparks ?? 0) });
      } else {
        onPurchased({ runes: Number(user?.runes ?? 0), sparks: Number(user?.sparks ?? 0) + grantAmount });
      }
    }

    // 4) close offer modal
    hideOffer();
  } else if (status === "failed") {
    setBuyError("Payment failed. Try again.");
  } else if (status === "cancelled") {
    setBuyError("Payment cancelled.");
  }
});

      } else {
        // fallback
        window.open(invoiceLink, "_blank", "noopener,noreferrer");
      }

      // Optional: You can poll your own endpoint until paid (you already have it)
      // GET /stars/order/:id exists in telegramWebhook.js :contentReference[oaicite:8]{index=8}
      // (implement polling if you want to auto-update UI)

      return;
    }

    // ---- RUNES/SPARKS flow (existing) ----
    const res = await fetch(`${process.env.REACT_APP_API_URL}/api/shop/discounts/purchase`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ offerId }),
    });

    const data = await res.json().catch(() => ({}));

    if (!res.ok || !data?.success) {
      const msg =
        data?.error ||
        (res.status === 429 ? "Too many requests. Try again later." : "Failed to purchase offer.");
      setBuyError(msg);
      return;
    }

    if (typeof onPurchased === "function" && data?.user) {
  onPurchased(data.user);
}

const grantType = String(chosen?.TYPE ?? chosen?.type ?? "RUNES").toUpperCase();
const grantAmount = Number(chosen?.GRANT_AMOUNT ?? chosen?.grantAmount ?? 0);

const prettyGrantType = String(grantType || "").toLowerCase().replace(/^./, c => c.toUpperCase());
setSuccessText(`[gold]${grantAmount} ${prettyGrantType}[/gold] Successfully Added To Your Account!`);

setSuccessOpen(true);

hideOffer();
  } catch (e) {
    setBuyError("Network error. Try again.");
  } finally {
    setBuying(false);
  }
};


  useEffect(() => {
    if (!cfgEnabled) return;
    if (!userId) return;
    if (!token) return;
    if (blocked) return;
    if (open) return;

    //console.log("[DiscountOfferModal] cfgEnabled:", cfgEnabled, "offers:", offers);
    //console.log("[DiscountOfferModal] user:", { id: userId, runes: user?.runes, sparks: user?.sparks });


    let cancelled = false;

    (async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/api/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json().catch(() => ({}));
        const serverNowMs = getServerNowMs(res, data);
        const now = serverNowMs ?? Date.now();

        if (cancelled) return;

        const next = pickEligibleOffer(now);


        if (!next) return;

        setChosen(next);
        setOpen(true);

        const validForSeconds = Number(next?.VALID_FOR_SECONDS ?? next?.validForSeconds ?? 0);
if (Number.isFinite(validForSeconds) && validForSeconds > 0) {
  setExpiresAtMs(now + validForSeconds * 1000);
} else {
  setExpiresAtMs(null);
}

        markShown(next, now);
        try {
  const id = String(next?.ID ?? next?.id ?? "").trim();
  if (id) {
    await fetch(`${process.env.REACT_APP_API_URL}/api/shop/discounts/mark-shown`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ offerId: id }),
    });
  }
} catch {}

      } catch {
        // if /api/status fails, fall back to local time (still better than doing nothing)
        const now = Date.now();
        const next = pickEligibleOffer(now);
        if (!next) return;

        if (!cancelled) {
          setChosen(next);
          setOpen(true);

          const validForSeconds = Number(next?.VALID_FOR_SECONDS ?? next?.validForSeconds ?? 0);
if (Number.isFinite(validForSeconds) && validForSeconds > 0) {
  setExpiresAtMs(now + validForSeconds * 1000);
} else {
  setExpiresAtMs(null);
}

          markShown(next, now);
          try {
  const id = String(next?.ID ?? next?.id ?? "").trim();
  if (id) {
    await fetch(`${process.env.REACT_APP_API_URL}/api/shop/discounts/mark-shown`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ offerId: id }),
    });
  }
} catch {}

        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    cfgEnabled,
    offers,
    userId,
    user?.runes,
    user?.sparks,
    blocked,
    open,
    token,
  ]);

  if (!open && !successOpen) return null;

  const safeChosen = chosen || {};
const title = String(safeChosen.TITLE ?? safeChosen.title ?? "").trim();
const subtitle = String(safeChosen.SUBTITLE ?? safeChosen.subtitle ?? "").trim();
const content = String(safeChosen.CONTENT ?? safeChosen.content ?? "").trim();
const footer = String(safeChosen.FOOTER_TEXT ?? safeChosen.footerText ?? safeChosen.footer ?? "").trim();
const avatar = String(safeChosen.AVATAR ?? safeChosen.avatar ?? "").trim();
  

  const remainingMs = expiresAtMs ? Math.max(0, expiresAtMs - Date.now()) : null;
void tick;


const formatCountdown = (ms) => {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;

  if (h > 0) {
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  }
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

const countdownText = remainingMs != null ? formatCountdown(remainingMs) : null;

if (successOpen) {
  return (
    <div className="modal-backdrop" onClick={() => setSuccessOpen(false)}>
      <motion.div
        className="modal fancy-success-modal"
        initial={{ scale: 0.85, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: "spring", stiffness: 300, damping: 22 }}
        onClick={(e) => e.stopPropagation()}
        style={{ textAlign: "center", padding: "18px 14px" }}
      >

        <img
  src="/finger.png"
  alt="Success"
  style={{ height: "80px", marginBottom: "5px", display: "inline-block" }}
  onError={(e) => console.log("finger.png failed", e)}
/>

        
        <h2 style={{ color: "#32cd32", marginBottom: "15px" }}>
              Transaction Completed!
        </h2>

        <div
          style={{
            marginBottom: 14,
            padding: "14px",
            borderRadius: 12,
            background: "rgba(0,0,0,0.35)",
            border: "1px solid rgba(255,255,255,0.16)",
            color: "#ddd",
            textShadow: "1px 1px #000",
            fontSize: 18,
            lineHeight: 1.35,
          }}
        >
          {renderRichText(successText)}
        </div>

        <div className="modal-footer" style={{ marginTop: 0 }}>
          <button onClick={() => setSuccessOpen(false)}>Close</button>
        </div>
      </motion.div>
    </div>
  );
}


  return (
    <div className="modal-backdrop" onClick={close}>
      <motion.div
        className="modal fancy-success-modal"
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: "spring", stiffness: 300, damping: 20 }}
        style={{ textAlign: "center", paddingRight: "12px", paddingLeft: "12px" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Avatar */}
        {avatar ? (
          <img
            src={avatar}
            alt=""
            style={{
              width: 84,
              height: 84,
              borderRadius: 999,
              objectFit: "cover",
              marginBottom: 12,
              border: "1px solid rgba(255,255,255,0.25)",
              background: "rgba(0,0,0,0.25)",
            }}
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
        ) : null}

        {/* Title */}
        {title ? (
          <h2 className="modal-title" style={{ marginBottom: 12 }}>
            {renderRichText(title)}
          </h2>
        ) : null}

        {/* Subtitle */}
        {subtitle ? (
          <p style={{ color: "#ddd", textShadow: "1px 1px #000", lineHeight: 1.35, marginBottom: 10 }}>
            {renderRichText(subtitle)}
          </p>
        ) : null}

        {countdownText ? (
  <div
    style={{
      marginBottom: 12,
      padding: "10px 12px",
      borderRadius: 12,
      background: "rgba(255,255,255,0.08)",
      border: "1px solid rgba(255,255,255,0.16)",
      textShadow: "1px 1px #000",
      fontSize: 16,
      display: "inline-block",
    }}
  >
    ⏳ Offer Ends in <b>{countdownText}</b>
  </div>
) : null}


        {/* Content */}
        {content ? (
          <div
            style={{
              marginBottom: 12,
              padding: "14px 14px",
              borderRadius: 12,
              background: "rgba(0,0,0,0.35)",
              border: "1px solid rgba(255,255,255,0.16)",
              color: "#ddd",
              textShadow: "1px 1px #000",
              lineHeight: 1.35,
              fontSize: 20,
              textAlign: "center",
              whiteSpace: "pre-wrap",
            }}
          >
            {renderRichText(content)}
          </div>
        ) : null}

        {/* Footer */}
        {footer ? (
          <div style={{ marginBottom: 12, opacity: 0.85, fontSize: 16 }}>
            {renderRichText(footer)}
          </div>
        ) : null}

        {buyError ? (
  <div style={{ marginBottom: 10, color: "#ffb3b3", textShadow: "1px 1px #000" }}>
    {buyError}
  </div>
) : null}


        <div className="modal-footer" style={{ marginTop: 0 }}>
          <button type="button" onClick={purchaseOffer} disabled={buying}>
  {buying ? "Processing..." : "Get This Offer"}
</button>



          <button onClick={close}>Close</button>
        </div>
      </motion.div>
    </div>
  );
}
